<template>
  <section class="pinned-wrapper">
    <Globe
      :year="year"
      :globeActive="globeActive"
      :visibleMembers="visibleMembers"
    />
    <div class="container pinned-container">
      <aside class="pinned-info">
        <div class="year-container">
          <div class="year-label"><strong>1974</strong></div>
        </div>
        <EDTHumbnail :eds="reversedEDs" />
        <KeyFigures v-if="year <= 2022" :year="year" />
      </aside>
      <MemberList
        v-if="memberList.length > 0"
        :data="memberList"
        :year="year"
      />
    </div>
  </section>
</template>

<script>
import gsap from "gsap";
import EDTHumbnail from "./EDTHumbnail.vue";
import ISO3 from "../../assets/ISO3.json";
import Globe from "../timeline/Globe.vue";
import MemberList from "./MemberList.vue";
import KeyFigures from "./KeyFigures.vue";
export default {
  components: {
    EDTHumbnail,
    MemberList,
    Globe,
    KeyFigures,
  },
  data() {
    return {
      futureEDs: null,
      reversedEDs: null,
      currentEd: null,
      memberList: [],
      dimensions: { width: null, height: null },
      screenHeight: null,
    };
  },
  props: {
    year: {
      required: true,
    },
    eds: {
      required: true,
    },
    members: {
      required: true,
    },
    globeActive: {
      type: Boolean,
    },
    visibleMembers: {
      type: Array,
    },
  },
  created() {
    window.addEventListener("resize", this.setScreenDimension);
  },
  destroyed() {
    window.removeEventListener("resize", this.setScreenDimension);
  },
  mounted() {
    this.formatEds();
    this.formatMembers();
  },
  watch: {
    year(curr, prev) {
      const { futureEDs } = this;
      // Show year
      if (curr && !prev) this.showYear();
      this.updateYear(curr);
      this.currentEd = futureEDs.find((d, i, self) => {
        return i === self.length - 1
          ? d.year <= curr
          : d.year <= curr && d.max > curr;
      });
    },
    currentEd(curr, prev) {
      if (curr) this.revealEd(curr);
    },
    dimensions(curr, prev) {
      if (curr.height < 700 && prev.height > 700) {
        this.revealEd(this.currentEd);
      }
      if (curr.height > 700 && prev.height < 700) {
        this.revealEd(this.currentEd);
      }
      if (curr.width > 959 && prev.width < 959) {
        this.revealEd(this.currentEd);
      }
      if (curr.width < 959 && prev.width > 959) {
        this.revealEd(this.currentEd);
      }
    },
  },
  methods: {
    setScreenDimension(e) {
      this.dimensions = {
        width: e.target.innerWidth,
        height: e.target.innerHeight,
      };
    },
    showYear() {
      gsap.to(".year-label", {
        opacity: 1,
        y: 0,
        duration: 0.3,
      });
    },
    updateYear(year) {
      gsap.to(".year-label", {
        textContent: year,
        snap: { textContent: 1 },
        ease: "power1.in",
        onStart: function (e) {
          this._targets[0].style.color = "#e3e3e3";
        },
        onComplete: function (e) {
          this._targets[0].style.color = "#04f";
        },
      });
    },
    revealEd(ed) {
      if (!ed) return;
      gsap.to(".ed--card-wrapper", {
        y: function () {
          const children = this._targets[0].children;
          const height = children[0].offsetHeight;
          return -((children.length - 1) * height) + ed.id * height;
        },
      });
    },
    formatMembers() {
      const { members } = this;
      let result = [];
      Object.entries(members).forEach((d) => {
        let year = d[0];
        let country = d[1];
        country.forEach((member, i) => {
          let name = ISO3.find((d) => d.ISO3 === member.ISO3).region;
          let item = { idx: i, year: Number(year), name, ISO3: member.ISO3 };
          let exists = result.find((d) => d.type === member.type);
          if (exists) {
            let empty = exists.cols.find((d) => d.data.length <= 20);
            empty
              ? empty.data.push(item)
              : exists.cols.unshift({ data: [item] });
          } else {
            result.push({ type: member.type, cols: [{ data: [item] }] });
          }
        });
      });
      this.memberList = result.reverse();
    },
    formatEds() {
      this.futureEDs = Object.entries(this.eds).map((d, i, self) => {
        let max = i < self.length - 1 ? self[i + 1][0] : "";
        return {
          id: i,
          name: d[1].name,
          img: d[1].img,
          year: Number(d[0]),
          ...(max && { max: Number(max) }),
        };
      });
      this.reversedEDs = [...this.futureEDs].reverse();
    },
  },
};
</script>

<style>
.pinned-wrapper {
  position: sticky;
  display: flex;
  align-items: start;
  top: 0;
  left: 0;
  width: 100%;
  margin: 0;
  height: calc(100vh - 60px);
}
.pinned-container {
  display: flex;
  justify-content: space-between;
  position: relative;
}
.pinned-info {
  padding-top: 32px;
  height: auto;
}
.year-container {
  position: relative;
  width: 100%;
}
.year-label {
  opacity: 0;
  font-weight: 600;
  color: #04f;
  font-size: 6rem;
  line-height: 6rem;
  letter-spacing: -0.03em;
}
@media screen and (min-width: 1480px) {
  .pinned-info {
    width: calc((1384px / 6) * 2);
  }
}
@media screen and (min-width: 1280px) and (max-width: 1479px) {
  .pinned-info {
    width: calc((100vw - 96px) / 3);
  }
}
@media screen and (min-width: 990px) and (max-width: 1279px) {
  .pinned-info {
    width: calc(((100vw - 96px) / 3));
  }
}
@media screen and (min-width: 730px) and (max-width: 989px) {
  .pinned-info {
    width: 255px;
  }
  .year-label {
    font-size: 4.5rem;
    line-height: 4.5rem;
  }
}
@media screen and (max-width: 729px) {
  .year-label {
    font-size: 3.95rem;
    line-height: 3.95rem;
  }
  .pinned-info {
    width: 200px;
  }
}

/* Height */
@media screen and (min-width: 960px) and (max-height: 700px) {
  .year-label {
    font-size: 4.5rem;
    line-height: 4.5rem;
  }
}
</style>
